import { useCallback, useEffect, useRef, useState } from 'react';

const MUTE_KEY = 'firmeto-document-sound-muted';
type SoundStatus = 'ready' | 'blocked' | 'unsupported';

/** No permission prompts: Web Audio is created/resumed only from the visible control's click. */
export function useDocumentSound() {
  const [muted, setMuted] = useState(() => {
    try { return window.localStorage.getItem(MUTE_KEY) === 'true'; } catch { return false; }
  });
  const audio = useRef<AudioContext | null>(null);
  const [status, setStatus] = useState<SoundStatus>(() => typeof window.AudioContext === 'function' ? 'blocked' : 'unsupported');
  const preference = useCallback((value: boolean) => {
    setMuted(value);
    try { window.localStorage.setItem(MUTE_KEY, String(value)); } catch { /* Session-only preference. */ }
  }, []);
  const mute = useCallback(() => preference(true), [preference]);
  const enable = useCallback(async () => {
    preference(false);
    if (typeof window.AudioContext !== 'function') { setStatus('unsupported'); return; }
    try {
      const context = audio.current ??= new window.AudioContext();
      context.onstatechange = () => {
        if (audio.current === context) setStatus(context.state === 'running' ? 'ready' : 'blocked');
      };
      await context.resume();
      if (audio.current === context) setStatus(context.state === 'running' ? 'ready' : 'blocked');
    } catch { setStatus('blocked'); }
  }, [preference]);
  const play = useCallback(() => {
    if (muted) return;
    const context = audio.current;
    if (!context || context.state !== 'running') {
      setStatus(typeof window.AudioContext === 'function' ? 'blocked' : 'unsupported');
      return;
    }
    try {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const now = context.currentTime;
      oscillator.frequency.setValueAtTime(880, now);
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.onended = () => { oscillator.disconnect(); gain.disconnect(); };
      oscillator.start(now);
      oscillator.stop(now + 0.3);
    } catch { setStatus('blocked'); }
  }, [muted]);
  useEffect(() => () => {
    const context = audio.current;
    audio.current = null;
    if (context) {
      context.onstatechange = null;
      void context.close().catch(() => {});
    }
  }, []);
  return { status, muted, enable, mute, play };
}
